<script lang="ts">
  import ChatSession from "../../components/Chat/ChatSession.svelte";
  import ChatTabs from "../../components/Chat/ChatTabs.svelte";
  import type { ChatTab } from "../../components/Chat/ChatTabs.svelte";
  import ReengageTab from "../../components/Chat/ReengageTab.svelte";
  import WinbackTab from "../../components/Chat/WinbackTab.svelte";
  import {
    chatSessions,
    chatSessionsCount,
    clientChatSessionId,
    notSeenSessionIds,
    unreadMessages,
  } from "../../stores/chatSessionsStore";
  import { CheckboxComponent } from "ui";
  import { showOnlyUnreadChat } from "../../stores/chatSessionsStore";
  import { getWithJwt, isClient, serverlessRoutes, translate, errorTracker } from "lib";
  import { Capacitor } from "@capacitor/core";
  import { onDestroy, onMount } from "svelte";
  import { isLoadingMore } from "../../stores/isLoadingMoreStore";
  import ChatSessionSkeleton from "../../components/Chat/ChatSessionSkeleton.svelte";
  import InfiniteScroll from "../../components/UI/InfiniteScroll.svelte";
  import { useSocket } from "../../lib/hooks/useSocket";
  import { user } from "stores";

  let scrollElem: HTMLDivElement;
  let isLoading: boolean = true;
  let skip = 0;
  let mutex = false;

  // Top-menu tab state. "chat" keeps the existing conversation list and is
  // the default; the Reengage / Winback tabs are trainer-only engagement
  // views and are never shown to clients.
  let selectedTab: ChatTab = "chat";

  chatSessions.subscribe((res) => {
    if (res.length > 0) {
      isLoading = false;
    }
  });

  const socket = useSocket({ componentName: 'ChatListScreen' });
  const {isConnected} = socket;
  
  // Subscribe to socket events when connected
  $: if ($isConnected && $user) {
    socket.emit("subscribe", { event: "session", id: $user.id });
    socket.emit("subscribe", { event: "setSeen", id: $user.id });
    
    socket.on("setSeen", (data) => {
      if (data.sessionId && data.notSeen === 0) {
        $notSeenSessionIds = $notSeenSessionIds.filter((id) => id !== data.sessionId);
      } else if (data.sessionId && data.notSeen > 0) {
        $notSeenSessionIds = [...$notSeenSessionIds, data.sessionId];
      }
      $chatSessions = $chatSessions.map((session) => {
        if (session.sessionId === data.sessionId) {
          return {
            ...session,
            notSeen: data.notSeen,
          };
        }
        return session;
      });
    });

    socket.on("session", (updatedSession) => {
      $notSeenSessionIds = [...$notSeenSessionIds, updatedSession.id];
      const index = $chatSessions.findIndex(
        (session) => session.sessionId === updatedSession.id
      );
      if (index === -1) {
        $chatSessions = [updatedSession, ...$chatSessions];
      } else {
        const mergedSession = {
          ...$chatSessions[index],
          ...updatedSession,
        };
        $chatSessions.splice(index, 1, mergedSession);
        $chatSessions.sort((a, b) => {
          const aDate = new Date(a.updatedAt);
          const bDate = new Date(b.updatedAt);
          return bDate - aDate;
        });
        $chatSessions = $chatSessions;
      }
    });
  }

  const toggle = () => {
    $showOnlyUnreadChat = $showOnlyUnreadChat === 1 ? 0 : 1;
  };

  const fetchChatSessionsMount = async () => {
    const url = isClient($user)
      ? serverlessRoutes.FETCH_CLIENT_SESSION
      : `${serverlessRoutes.SESSION}?take=15&skip=${skip}&unread=${$showOnlyUnreadChat}`;
    if (mutex) return;
    mutex = true;
    try {
      isLoadingMore.set(true);
      const data = await getWithJwt(url);
      if (isClient($user)) {
        $clientChatSessionId = data.data.sessionId;
      } else {
        if (skip === 0) $chatSessions = [];
        $chatSessions = [...$chatSessions, ...data.data.chatSessions];
        if ($chatSessionsCount === 0) {
          $chatSessionsCount = data.data.chatSessionsCount;
        }
        $notSeenSessionIds = data.data.notSeenSessionIds;
        skip += 15;
      }
      $unreadMessages = data.data.notSeenCount;
    } catch (err) {
    } finally {
      isLoadingMore.set(false);
      mutex = false;
    }
  };

  onMount(() => {
    fetchChatSessionsMount();
  });

  onDestroy(() => {
    if ($isConnected && $user) {
      socket.emit("unsubscribe", { event: "session", id: $user.id });
      socket.emit("unsubscribe", { event: "setSeen", id: $user.id });
    }
    $isLoadingMore = false;
  });
</script>

<div
  class={`bg-white dark:bg-zinc-800 text-slate-900 dark:text-slate-100 ${Capacitor.isNativePlatform() ? "chat-container" : ""}`}
  bind:this={scrollElem}
>
  {#if Capacitor.getPlatform() === "ios"}
    <div class="mt-20" />
  {/if}

  {#if !isClient($user) && $user?.id === 13}
    <ChatTabs bind:selectedTab />
  {/if}

  {#if selectedTab === "chat"}
    <div class="flex flex-row p-3 ml-1">
      <div on:click={() => toggle()}>
        <CheckboxComponent value={$showOnlyUnreadChat === 1 ? true : false} />
      </div>
      <span class="ml-2">
        {translate("SHOW_ONLY_UNREAD")}
      </span>
    </div>
    {#if isLoading}
      <div class="flex-col center-center">
        <ChatSessionSkeleton />
        <ChatSessionSkeleton />
        <ChatSessionSkeleton />
        <ChatSessionSkeleton />
        <ChatSessionSkeleton />
        <ChatSessionSkeleton />
        <ChatSessionSkeleton />
        <ChatSessionSkeleton />
        <ChatSessionSkeleton />
        <ChatSessionSkeleton />
        <ChatSessionSkeleton />
      </div>
    {:else}
      <div class="flex-col center-center">
        {#each $chatSessions as session}
          <ChatSession {session} />
        {/each}
      </div>
      <!-- {#if $chatSessions.length < $chatSessionsCount}
        <LoadMoreButton fetchMoreData={Mount} />
        <div class="m-3"></div>
      {/if} -->
      <InfiniteScroll
        hasMore={$chatSessions.length < $chatSessionsCount}
        on:loadMore={fetchChatSessionsMount}
      />
    {/if}
  {:else if selectedTab === "reengage"}
    <ReengageTab />
  {:else if selectedTab === "winback"}
    <WinbackTab />
  {/if}
</div>

<style>
  .chat-container {
    min-height: 100%;
    margin-top: 48px;
  }
</style>
